import { z } from 'zod';
import type { UserRole } from '../enums/user-role.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

/**
 * Magasins d'un réseau de boutiques (réunion du 09/10/2026).
 *
 * Les gros réseaux (LCT : 13 superviseurs, 30 vendeurs ; AMS : 18 magasins)
 * déplacent leurs RPV (= superviseurs) et vendeurs d'un magasin à l'autre,
 * parfois pour une demi-journée. Ils veulent la perf ET la facturation par
 * établissement, et toujours la perf par RPV. D'où :
 *
 *  1. un **magasin** (`Store`) par établissement physique, propre au réseau ;
 *  2. l'**affectation** d'une personne à un ou plusieurs magasins, décidée
 *     par le back-office (`STORE_MANAGER_ROLES`), jamais déclarée librement ;
 *  3. un choix à la connexion **seulement** si plusieurs magasins sont
 *     ouverts à la personne — un seul magasin, aucun choix ;
 *  4. le magasin **figé** sur la fiche à sa création (`Lead.storeId`) ;
 *  5. la **réattribution** d'une fiche (magasin, vendeur, superviseur,
 *     manager) par le back-office seulement — jamais le manager — historisée
 *     dans la timeline (`reattributed`).
 */

/** Qui gère les magasins, les affectations et la réattribution des fiches. */
export const STORE_MANAGER_ROLES = [
  'admin',
  'responsable_partenaire',
  'responsable_backoffice',
] as const satisfies readonly UserRole[];

export function canManageStores(role: UserRole | null | undefined): boolean {
  return !!role && (STORE_MANAGER_ROLES as readonly UserRole[]).includes(role);
}

/**
 * Rôles qu'on affecte à un magasin. Le manager est inclus : certains réseaux
 * ont un compte manager par boutique (« Manager AURILLAC »). RP et back-office
 * voient tout le réseau, un magasin ne leur dirait rien.
 */
export const STORE_ASSIGNABLE_ROLES = [
  'manager',
  'superviseur',
  'vendeur',
] as const satisfies readonly UserRole[];

export function isStoreAssignableRole(role: UserRole | null | undefined): boolean {
  return !!role && (STORE_ASSIGNABLE_ROLES as readonly UserRole[]).includes(role);
}

/** Rôles qui peuvent porter une fiche (`Lead.commercialId`) après réattribution. */
export const LEAD_COMMERCIAL_ROLES = [
  'manager',
  'superviseur',
  'vendeur',
] as const satisfies readonly UserRole[];

const StoreNameSchema = z.string().trim().min(1, 'Nom obligatoire').max(100);
const StoreCodeSchema = z.string().trim().max(50);
const StoreAddressSchema = z.string().trim().max(255);

/** Une personne affectée à un magasin (affectation active). */
export const StoreAssigneeSchema = z.object({
  id: UuidV7Schema,
  firstName: z.string(),
  lastName: z.string(),
  role: z.string(),
  matricule: z.string(),
  partnerId: UuidV7Schema,
  assignedAt: IsoDateStringSchema,
});
export type StoreAssignee = z.infer<typeof StoreAssigneeSchema>;

export const StoreSchema = z.object({
  id: UuidV7Schema,
  partnerId: UuidV7Schema,
  name: z.string(),
  /** Code point de vente chez l'opérateur (ex. « code Orient »). */
  externalCode: z.string().nullable(),
  address: z.string().nullable(),
  archived: z.boolean(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
  /** Nombre de personnes affectées aujourd'hui. */
  assignedCount: z.number().int().min(0),
  /** Détail des affectés — renvoyé seulement à qui gère les magasins. */
  assignees: z.array(StoreAssigneeSchema).optional(),
});
export type Store = z.infer<typeof StoreSchema>;

/** Le strict nécessaire pour un bandeau ou un sélecteur. */
export const StoreSummarySchema = z.object({
  id: UuidV7Schema,
  name: z.string(),
  externalCode: z.string().nullable(),
});
export type StoreSummary = z.infer<typeof StoreSummarySchema>;

export const StoreListQuerySchema = z.object({
  /** Admin seulement : le réseau dont on veut les magasins. */
  partnerId: UuidV7Schema.optional(),
  /** `true` = archivés compris. */
  includeArchived: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type StoreListQuery = z.infer<typeof StoreListQuerySchema>;

export const CreateStoreSchema = z.object({
  /** Admin seulement ; ignoré pour un RP / back-office (son propre réseau). */
  partnerId: UuidV7Schema.optional(),
  name: StoreNameSchema,
  externalCode: StoreCodeSchema.nullable().optional(),
  address: StoreAddressSchema.nullable().optional(),
});
export type CreateStoreInput = z.infer<typeof CreateStoreSchema>;

export const UpdateStoreSchema = z
  .object({
    name: StoreNameSchema.optional(),
    externalCode: StoreCodeSchema.nullable().optional(),
    address: StoreAddressSchema.nullable().optional(),
    archived: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Rien à modifier' });
export type UpdateStoreInput = z.infer<typeof UpdateStoreSchema>;

/** Body de `PUT /users/:id/stores` : l'ensemble EXACT des magasins actifs. */
export const SetUserStoresSchema = z.object({
  storeIds: z.array(UuidV7Schema).max(100),
});
export type SetUserStoresInput = z.infer<typeof SetUserStoresSchema>;

/** Une ligne d'historique d'affectation (on ne supprime jamais). */
export const StoreAssignmentSchema = z.object({
  id: UuidV7Schema,
  storeId: UuidV7Schema,
  storeName: z.string(),
  storeArchived: z.boolean(),
  assignedAt: IsoDateStringSchema,
  assignedById: UuidV7Schema.nullable(),
  unassignedAt: IsoDateStringSchema.nullable(),
  unassignedById: UuidV7Schema.nullable(),
});
export type StoreAssignment = z.infer<typeof StoreAssignmentSchema>;

/** Réponse de `GET|PUT /users/:id/stores`. */
export const UserStoresResponseSchema = z.object({
  userId: UuidV7Schema,
  active: z.array(StoreSummarySchema),
  history: z.array(StoreAssignmentSchema),
});
export type UserStoresResponse = z.infer<typeof UserStoresResponseSchema>;

/**
 * Réponse de `GET /users/me/stores` — les magasins ouverts à l'APPAREIL : en
 * session PIN, ceux du superviseur de la tablette (`deviceUserId`), pas ceux
 * du vendeur actif. `retail = false` → pas de bandeau.
 */
export const MyStoresResponseSchema = z.object({
  retail: z.boolean(),
  deviceUserId: UuidV7Schema,
  stores: z.array(StoreSummarySchema),
  /**
   * Le réseau a-t-il au moins un magasin actif ? Migration au rythme de
   * chaque réseau : tant qu'il n'en a créé aucun, rien ne change pour le
   * terrain (pas de bandeau, pas d'alerte « aucun magasin »).
   */
  networkHasStores: z.boolean(),
});
export type MyStoresResponse = z.infer<typeof MyStoresResponseSchema>;

/**
 * Body de `PUT /stores/:id/assignees` : fixe l'ensemble des personnes
 * affectées à CE magasin (vue magasin, pour affecter en série). Mêmes règles
 * que `PUT /users/:id/stores`, n'agit que sur ce magasin : les autres
 * magasins des personnes ne bougent pas.
 */
export const SetStoreAssigneesSchema = z.object({
  userIds: z.array(UuidV7Schema).max(500),
});
export type SetStoreAssigneesInput = z.infer<typeof SetStoreAssigneesSchema>;

/** Body de `POST /stores/:id/claim-leads` — reprise des fiches passées. */
export const ClaimStoreLeadsSchema = z.object({
  superviseurId: UuidV7Schema,
});
export type ClaimStoreLeadsInput = z.infer<typeof ClaimStoreLeadsSchema>;

export const ClaimStoreLeadsResponseSchema = z.object({
  claimed: z.number().int().min(0),
});
export type ClaimStoreLeadsResponse = z.infer<typeof ClaimStoreLeadsResponseSchema>;

/** Motif posé sur les events de `claim-leads`. */
export const STORE_CLAIM_REASON = 'Rattachement initial';

// ── Réattribution d'une fiche ───────────────────────────────────────────

export const REATTRIBUTION_FIELDS = [
  'storeId',
  'commercialId',
  'superviseurId',
  'managerId',
] as const;
export type ReattributionField = (typeof REATTRIBUTION_FIELDS)[number];

export const REATTRIBUTION_FIELD_LABEL_FR: Record<ReattributionField, string> = {
  storeId: 'magasin',
  commercialId: 'vendeur',
  superviseurId: 'superviseur',
  managerId: 'manager',
};

/**
 * Body de `PATCH /leads/:id/attribution`. Un champ absent ne change pas ;
 * `null` le vide (sauf `commercialId`, obligatoire sur une fiche). Changer
 * le vendeur ne recalcule PAS sa chaîne : superviseur et manager restent
 * explicites (l'écran propose ceux du nouveau vendeur en pré-remplissage).
 */
export const LeadAttributionSchema = z.object({
  version: z.number().int().min(0),
  storeId: UuidV7Schema.nullable().optional(),
  commercialId: UuidV7Schema.optional(),
  superviseurId: UuidV7Schema.nullable().optional(),
  managerId: UuidV7Schema.nullable().optional(),
  reason: z.string().trim().max(500).optional(),
});
export type LeadAttributionInput = z.infer<typeof LeadAttributionSchema>;

/** Une ligne « avant → après » d'un event `reattributed`. */
export const ReattributionChangeSchema = z.object({
  field: z.enum(REATTRIBUTION_FIELDS),
  from: z.string().nullable(),
  to: z.string().nullable(),
  /**
   * Libellé lisible figé au moment du fait : nom du magasin, ou MATRICULE
   * pour une personne — jamais son nom : la meta d'un event échappe à
   * l'anonymisation admin, le matricule est l'identifiant B2B toujours visible.
   */
  fromLabel: z.string().nullable().optional(),
  toLabel: z.string().nullable().optional(),
});
export type ReattributionChange = z.infer<typeof ReattributionChangeSchema>;

export const ReattributedMetaSchema = z.object({
  changes: z.array(ReattributionChangeSchema),
  reason: z.string().nullable().optional(),
});
export type ReattributedMeta = z.infer<typeof ReattributedMetaSchema>;
